import React, { useState, useEffect } from 'react';
import logo from '@/assets/Logo.png';
import { useJiraBoard } from './api/jira-board';

interface Tool {
  name: string;
  icon: string;
  href: string;
}

interface DocLink {
  title: string;
  href: string;
}

// Tools and repositories data
const toolsData: Tool[] = [
  { 
    name: 'SocialSparkApp - GitHub', 
    icon: '📦', 
    href: 'https://github.com/SocialSparkApp'
  },
  { 
    name: 'Jira', 
    icon: '🎯', 
    href: 'https://socialsparkapp.atlassian.net/jira/software/projects/SSA/boards/1'
  },
  { 
    name: 'Confluence', 
    icon: '📚', 
    href: 'https://socialsparkapp.atlassian.net/wiki/spaces/S/overview?homepageId=3997803'
  },
];

// Documentation links
const docLinks: DocLink[] = [
  { title: 'Onboarding/Training', href: 'https://socialsparkapp.atlassian.net/wiki/spaces/S/pages/3997803/Onboarding' },
  { title: 'Architecture', href: 'https://socialsparkapp.atlassian.net/wiki/spaces/S/pages/3997804/Architecture' },
  { title: 'Database Access', href: 'https://socialsparkapp.atlassian.net/wiki/spaces/S/pages/3997806/Database+Schema' },
  { title: 'API Documentation', href: 'https://socialsparkapp.atlassian.net/wiki/spaces/S/pages/3997807/API+Reference' },
  { title: 'Deployment Guide', href: 'https://socialsparkapp.atlassian.net/wiki/spaces/S/pages/3997808/Deployment+Guide' },
];

// Tech stack with better icons
const techStack = [
  { name: 'React', icon: '⚛️', category: 'Frontend', color: 'from-blue-500 to-cyan-400' },
  { name: 'Python', icon: '🟢', category: 'Backend', color: 'from-green-500 to-emerald-400' },
  { name: 'PostgreSQL', icon: '🐘', category: 'Database', color: 'from-blue-600 to-indigo-400' },
  { name: 'AWS', icon: '☁️', category: 'Cloud', color: 'from-orange-500 to-yellow-400' },
  // { name: 'Docker', icon: '🐳', category: 'DevOps', color: 'from-blue-400 to-cyan-300' },
  { name: 'GitHub Actions', icon: '⚙️', category: 'CI/CD', color: 'from-gray-700 to-gray-500' },
];

function Engineering(): React.ReactElement {
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const { data: jiraBoard, loading: jiraLoading, error: jiraError } = useJiraBoard();

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Active Epics in progress
  const activeEpics = jiraBoard
    ? jiraBoard.issues.filter(i => i.type?.name === 'Epic' && i.status?.category === 'In Progress')
    : [];

  // Recent completed issues
  const recentActivity = jiraBoard
    ? jiraBoard.issues
        .filter(i => i.status.category === 'Done')
        .sort((a, b) => new Date(b.updated).getTime() - new Date(a.updated).getTime())
        .slice(0, 5)
    : [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      {/* Compact Hero Section */}
      <section className="relative overflow-hidden py-12 px-6">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#1e1b4b] via-[#312e81] to-[#1e1b4b]"></div>
          <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_20%_50%,rgba(124,58,237,0.4)_0%,transparent_40%),radial-gradient(circle_at_80%_50%,rgba(236,72,153,0.3)_0%,transparent_40%)]"></div>
        </div>
        
        <div className="relative z-10 max-w-[1400px] mx-auto w-full">
          <div className="flex items-center justify-between">
            <div className="max-w-[700px]">
              <h1 className="font-display text-4xl font-extrabold text-white mb-4 leading-[1.1]">Engineering & Technology</h1>
              <p className="text-gray-300 leading-relaxed">
                This page is the central hub for our engineering work, tools, documentation, and technical ownership. 
                It provides visibility into current initiatives and serves as a reference for how we build and operate our technology.
              </p>
            </div>
            <div className="hidden lg:flex flex-col items-center">
              <img src={logo} alt="SocialSpark" className="w-32 h-32 object-cover rounded-full" />
              <span className="font-display text-xl font-extrabold text-white text-center leading-[1.1] tracking-wider mt-2">SOCIAL SPARK</span>
            </div>
          </div>
        </div>
      </section>

      {/* Compact Time Zones */}
      <section className="max-w-[1400px] mx-auto px-6 -mt-6 relative z-20">
        <div className="flex gap-4 max-w-[500px] ml-auto">
          <div className="flex-1 bg-white rounded-xl p-4 shadow-lg">
            <div className="text-xs text-gray-500 mb-1">Hartford, CT</div>
            <div className="font-display text-2xl font-bold text-gray-900 leading-none">
              {currentTime.toLocaleTimeString('en-US', { 
                hour: 'numeric', 
                minute: '2-digit',
                hour12: true,
                timeZone: 'America/New_York'
              })}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {currentTime.toLocaleDateString('en-US', { 
                month: 'numeric',
                day: 'numeric',
                year: 'numeric',
                timeZone: 'America/New_York'
              })}
            </div>
          </div>
          <div className="flex-1 bg-white rounded-xl p-4 shadow-lg relative">
            <div className="text-xs text-gray-500 mb-1">Tempe, AZ</div>
            <div className="font-display text-2xl font-bold text-gray-900 leading-none">
              {currentTime.toLocaleTimeString('en-US', { 
                hour: 'numeric', 
                minute: '2-digit',
                hour12: true,
                timeZone: 'America/Phoenix'
              })}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {currentTime.toLocaleDateString('en-US', { 
                month: 'numeric',
                day: 'numeric',
                year: 'numeric',
                timeZone: 'America/Phoenix'
              })}
            </div>
            <span className="absolute top-2 right-2 text-[10px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">2h behind</span>
          </div>
        </div>
      </section>

      {/* Main Content Grid */}
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Active Epics */}
          <div className="lg:col-span-2 space-y-8">
            {/* Active Epics Card */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Active Epics</h2>
                  <p className="text-sm text-gray-600 mt-1">Major initiatives currently in progress</p>
                </div>
                <a 
                  href="https://socialsparkapp.atlassian.net/jira/software/projects/SSA/boards/1" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium group"
                >
                  View in Jira
                  <span className="transition-transform group-hover:translate-x-1">→</span>
                </a>
              </div>
              
              <div className="p-6">
                {jiraLoading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mb-4"></div>
                    <p className="text-gray-500">Loading board...</p>
                  </div>
                ) : jiraError ? (
                  <div className="bg-red-50 rounded-xl p-6 text-center">
                    <span className="text-3xl mb-3 block">⚠️</span>
                    <p className="text-red-800 font-semibold mb-2">Error loading Jira board</p>
                    <p className="text-sm text-red-600">{jiraError}</p>
                  </div>
                ) : activeEpics.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl">🎉</span>
                    </div>
                    <p className="text-gray-500">No active epics in progress</p>
                    <p className="text-sm text-gray-400 mt-1">Everything is on track!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {activeEpics.map(epic => (
                      <a
                        key={epic.key}
                        href={`https://socialsparkapp.atlassian.net/browse/${epic.key}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block group"
                      >
                        <div className="bg-gradient-to-r from-gray-50 to-white rounded-xl p-5 hover:shadow-lg transition-all duration-300 border border-gray-200 group-hover:border-purple-200">
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                              <img src={epic.type.iconUrl} alt={epic.type.name} className="w-6 h-6" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <div className="font-mono font-bold text-purple-600 text-lg">{epic.key}</div>
                                  <h3 className="font-semibold text-gray-900 mt-1 line-clamp-2 group-hover:text-purple-700 transition-colors">
                                    {epic.summary}
                                  </h3>
                                </div>
                                {epic.storyPoints && (
                                  <div className="text-sm font-medium px-3 py-1 bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 rounded-full border border-purple-100">
                                    {epic.storyPoints} pts
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex items-center justify-between mt-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white flex items-center justify-center text-sm font-bold">
                                    {epic.assignee?.name?.charAt(0) || '?'}
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    {epic.assignee?.name?.split(' ')[0] || 'Unassigned'}
                                  </div>
                                </div>
                                
                                <div className="text-xs text-gray-500 flex items-center gap-2">
                                  <span>Updated {new Date(epic.updated).toLocaleDateString()}</span>
                                  <span className="opacity-0 group-hover:opacity-100 transition-opacity">🔗</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Recent Activity Card */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Recent Activity</h2>
                  <p className="text-sm text-gray-600 mt-1">Latest completed work</p>
                </div>
                <a 
                  href="https://socialsparkapp.atlassian.net/jira/software/projects/SSA/boards/1" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium group"
                >
                  View all
                  <span className="transition-transform group-hover:translate-x-1">→</span>
                </a>
              </div>
              
              <div className="divide-y divide-gray-100">
                {recentActivity.length === 0 ? (
                  <div className="p-8 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl">📝</span>
                    </div>
                    <p className="text-gray-500">No recent activity to display</p>
                  </div>
                ) : (
                  recentActivity.map((issue) => (
                    <a
                      key={issue.key}
                      href={`https://socialsparkapp.atlassian.net/browse/${issue.key}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group block hover:bg-gray-50 transition-colors"
                    >
                      <div className="px-6 py-4">
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-100 to-emerald-100 flex items-center justify-center">
                              <span className="text-green-600 text-lg">✓</span>
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono font-bold text-purple-600">{issue.key}</span>
                              <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                                Completed
                              </span>
                            </div>
                            <p className="text-gray-900 font-medium truncate group-hover:text-purple-700 transition-colors">
                              {issue.summary}
                            </p>
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs">
                                  {issue.assignee?.name?.charAt(0) || '?'}
                                </div>
                                {issue.assignee?.name?.split(' ')[0] || 'Unassigned'}
                              </span>
                              <span>
                                {new Date(issue.updated).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric'
                                })}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </a>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Tools & Tech */}
          <div className="space-y-8">
            {/* Tools Card */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                  <span className="text-xl">🛠️</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Tools & Repositories</h3>
                  <p className="text-sm text-gray-600">Essential development resources</p>
                </div>
              </div>
              
              <div className="space-y-3">
                {toolsData.map((tool, index) => (
                  <a
                    key={index}
                    href={tool.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group"
                  >
                    <div className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all duration-300 group-hover:scale-[1.02]">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center">
                        <span className="text-2xl">{tool.icon}</span>
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                          {tool.name}
                        </div>
                      </div>
                      <span className="text-gray-400 group-hover:text-blue-500 transition-colors">→</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Documentation Card */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl shadow-xl border border-purple-100 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <span className="text-xl text-white">📚</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Documentation</h3>
                  <p className="text-sm text-gray-600">Confluence knowledge base</p>
                </div>
              </div>
              
              <div className="space-y-3">
                {docLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group"
                  >
                    <div className="flex items-center justify-between p-3 rounded-lg bg-white/80 hover:bg-white transition-all duration-300 group-hover:scale-[1.02]">
                      <span className="font-medium text-gray-900 group-hover:text-purple-600 transition-colors">
                        {link.title}
                      </span>
                      <span className="text-gray-400 group-hover:text-purple-500 transition-colors">→</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Tech Stack Card */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <span className="text-xl text-white">⚡</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Tech Stack</h3>
                  <p className="text-sm text-gray-300">Modern technologies we use</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                {techStack.map((tech, index) => (
                  <div
                    key={index}
                    className="group"
                  >
                    <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 hover:bg-gray-800 transition-all duration-300 hover:scale-[1.05] border border-gray-700/50">
                      <div className="flex flex-col items-center text-center">
                        <div className={`text-3xl mb-2 bg-gradient-to-br ${tech.color} bg-clip-text text-transparent`}>
                          {tech.icon}
                        </div>
                        <h4 className="font-bold text-white text-sm">{tech.name}</h4>
                        <div className="text-xs text-gray-400 mt-1">{tech.category}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Engineering;